import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useSearch } from "@/context/SearchContext";
import { Binoculars, Broadcast, Detective, Globe, Hash, LinkSimple } from "@phosphor-icons/react";

const IOC_ICON = { ip: Globe, domain: LinkSimple, hash: Hash, url: LinkSimple };

export default function ThreatIntel() {
  const [iocs, setIocs] = useState([]);
  const [actors, setActors] = useState([]);
  const [feeds, setFeeds] = useState([]);
  const [typeFilter, setTypeFilter] = useState("");
  const { query } = useSearch();

  const load = useCallback(async () => {
    const [i, a, f] = await Promise.all([api.get("/intel/iocs"), api.get("/intel/actors"), api.get("/intel/feeds")]);
    setIocs(i.data.iocs); setActors(a.data.actors); setFeeds(f.data.feeds);
  }, []);
  useEffect(() => { load(); }, [load]);

  const q = query.trim().toLowerCase();
  const filtered = iocs.filter((c) => (!typeFilter || c.type === typeFilter) && (!q || c.value.toLowerCase().includes(q) || (c.actor || "").toLowerCase().includes(q)));

  return (
    <div data-testid="intel-page">
      <div className="mb-6">
        <p className="text-[10px] uppercase tracking-[0.3em] text-[#00F0FF]">Threat Intelligence</p>
        <h1 className="font-head text-3xl tracking-tighter font-black mt-1">Threat Intel</h1>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-6" data-testid="intel-feeds">
        {feeds.map((f) => (
          <div key={f.name} className="bg-[#0C0C14] border border-[#1F1F33] p-3">
            <div className="flex items-center gap-1.5">
              <span className={`w-1.5 h-1.5 ${f.status === "connected" ? "bg-[#3ECF8E]" : "bg-[#FF9500]"}`} />
              <span className="text-[10px] uppercase tracking-wider text-[#71717A] truncate">{f.name}</span>
            </div>
            <div className="font-head text-lg font-black mt-1">{f.indicators.toLocaleString()}</div>
            <div className="text-[9px] text-[#71717A]">synced {f.last_sync}</div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        <div className="xl:col-span-2">
          <div className="flex items-center gap-2 mb-3 flex-wrap">
            <Detective size={16} color="#00F0FF" />
            <span className="text-[10px] uppercase tracking-[0.2em] text-[#71717A]">Indicators of Compromise</span>
            <div className="flex gap-1.5 ml-auto">
              {["", "ip", "domain", "hash", "url"].map((t) => (
                <button key={t || "all"} data-testid={`ioc-filter-${t || "all"}`} onClick={() => setTypeFilter(t)}
                  className={`text-[10px] uppercase px-2 py-1 border ${typeFilter === t ? "border-[#00F0FF] text-[#00F0FF]" : "border-[#1F1F33] text-[#71717A]"}`}>{t || "all"}</button>
              ))}
            </div>
          </div>
          <div className="bg-[#0C0C14] border border-[#1F1F33]" data-testid="ioc-list">
            {filtered.map((c) => {
              const Icon = IOC_ICON[c.type] || Globe;
              return (
                <div key={c.id} data-testid={`ioc-${c.id}`} className="flex items-center gap-3 px-4 py-2.5 border-b border-[#1F1F33]">
                  <Icon size={15} color="#71717A" className="shrink-0" />
                  <span className="font-mono text-xs text-[#A1A1AA] flex-1 min-w-0 truncate">{c.value}</span>
                  <span className="text-[10px] uppercase text-[#FF9500] hidden sm:block">{c.threat_type}</span>
                  <span className="text-[10px] text-[#71717A] hidden md:block">{c.actor}</span>
                  <span className="text-[10px] font-mono tabular-nums" style={{ color: c.confidence >= 85 ? "#FF3B30" : "#FFCC00" }}>{c.confidence}%</span>
                </div>
              );
            })}
            {filtered.length === 0 && <div className="text-xs text-[#71717A] py-8 text-center">No indicators match.</div>}
          </div>
        </div>

        <div className="xl:col-span-1">
          <div className="flex items-center gap-2 mb-3"><Binoculars size={16} color="#FF3B30" /><span className="text-[10px] uppercase tracking-[0.2em] text-[#71717A]">Tracked Actors</span></div>
          <div className="space-y-2" data-testid="intel-actors">
            {actors.map((a) => (
              <div key={a.id} className="bg-[#0C0C14] border border-[#1F1F33] p-4">
                <div className="flex items-center justify-between">
                  <span className="font-head font-bold text-sm">{a.name}</span>
                  {a.active && <span className="w-2 h-2 bg-[#FF3B30] pulse-dot" />}
                </div>
                <div className="text-[11px] text-[#71717A] mt-1">{a.origin} · {a.motivation}</div>
                <div className="text-[10px] text-[#71717A] mt-1">aka {a.aliases.join(", ")}</div>
                <div className="flex flex-wrap gap-1 mt-2">
                  {a.targets.map((t) => <span key={t} className="text-[9px] uppercase tracking-wider text-[#00F0FF] border border-[#00F0FF]/30 px-1.5 py-0.5">{t}</span>)}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
